import { useState, useEffect, useCallback, useMemo } from 'react';

// SWA built-in auth hook.
// Replaces @azure/msal-react's useMsal() — instead of running an OAuth flow
// in the browser, we let Static Web Apps handle everything server-side and
// just read the result from /.auth/me.
//
// /.auth/me returns { clientPrincipal: { userId, userDetails, userRoles,
//   identityProvider, claims } } when signed in, { clientPrincipal: null }
// when not. SWA sets/clears the session cookie automatically on /.auth/login
// and /.auth/logout.

function buildLoginUrl(provider = 'aad', returnTo) {
    const post = returnTo || (typeof window !== 'undefined'
        ? (window.location.pathname + window.location.search)
        : '/');
    return `/.auth/login/${provider}?post_login_redirect_uri=${encodeURIComponent(post)}`;
}

function buildLogoutUrl(returnTo = '/') {
    return `/.auth/logout?post_logout_redirect_uri=${encodeURIComponent(returnTo)}`;
}

let _cache = null; // simple module-level cache so multiple components share one fetch

export function useAuth() {
    const [state, setState] = useState(() => ({
        loading: _cache === null,
        account: _cache ? _cache.account : null
    }));

    useEffect(() => {
        if (_cache !== null) return; // already loaded
        let cancelled = false;

        // Local dev bypass: when npm start runs against localhost:3000, /.auth/me
        // doesn't exist (it's a SWA-only endpoint). Skip the fetch and pretend
        // we're signed in as the developer so the rest of the app works.
        const isLocalDev = typeof window !== 'undefined'
            && window.location.hostname === 'localhost'
            && process.env.NODE_ENV === 'development';
        if (isLocalDev) {
            const email = (process.env.REACT_APP_DEV_USER_EMAIL || 'sayeeda.baqt@contexq.com').toLowerCase();
            const account = { username: email, name: email, homeAccountId: 'dev', identityProvider: 'dev', roles: ['authenticated'] };
            _cache = { account };
            setState({ loading: false, account });
            return () => { cancelled = true; };
        }

        fetch('/.auth/me', { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : null))
            .then(data => {
                const cp = data && data.clientPrincipal;
                const account = cp
                    ? {
                          username: (cp.userDetails || '').toLowerCase(),
                          name: cp.userDetails || '',
                          homeAccountId: cp.userId || '',
                          identityProvider: cp.identityProvider || '',
                          roles: cp.userRoles || []
                      }
                    : null;
                _cache = { account };
                if (!cancelled) setState({ loading: false, account });
            })
            .catch(() => {
                _cache = { account: null };
                if (!cancelled) setState({ loading: false, account: null });
            });

        return () => { cancelled = true; };
    }, []);

    const login = useCallback((provider = 'aad', returnTo) => {
        window.location.href = buildLoginUrl(provider, returnTo);
    }, []);

    const logout = useCallback((returnTo = '/') => {
        _cache = null; // force re-fetch on next mount
        window.location.href = buildLogoutUrl(returnTo);
    }, []);

    // Stable array reference: same `accounts` identity across renders unless
    // the underlying account actually changes. Without this useMemo, a parent
    // useEffect that depends on `accounts` would loop forever.
    const accounts = useMemo(
        () => (state.account ? [state.account] : []),
        [state.account]
    );

    return {
        loading: state.loading,
        account: state.account,
        accounts,
        isAuthenticated: !!state.account,
        login,
        logout
    };
}
